import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}

export const FormField: React.FC<FormFieldProps> = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#DC582A] focus:border-transparent"
    />
  </div>
);

interface FormTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#DC582A] focus:border-transparent resize-none"
    />
  </div>
);

interface FormSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

export const FormSelect: React.FC<FormSelectProps> = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#DC582A] focus:border-transparent"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

interface ToggleFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}

export const ToggleField: React.FC<ToggleFieldProps> = ({ label, checked, onChange, description }) => (
  <label className="flex items-center gap-3 cursor-pointer py-1">
    <div className="relative flex-shrink-0">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <div className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#DC582A]' : 'bg-gray-300'}`}>
        <div className={`w-4 h-4 bg-white rounded-full shadow absolute top-1 transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </div>
    </div>
    <div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {description && <p className="text-xs text-gray-400">{description}</p>}
    </div>
  </label>
);

interface StringListEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

export const StringListEditor: React.FC<StringListEditorProps> = ({ label, items, onChange, placeholder }) => {
  const updateItem = (index: number, value: string) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    onChange([...items, '']);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#DC582A] focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addItem}
          className="flex items-center gap-1 text-sm text-[#DC582A] hover:text-[#c24d24] font-medium"
        >
          <Plus size={16} />
          Ajouter
        </button>
      </div>
    </div>
  );
};

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  /** Nœud React placé à gauche du titre — utilisé pour la poignée DnD */
  dragHandle?: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({ title, children, defaultOpen = true, dragHandle }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="flex items-stretch">
        {dragHandle && (
          <div className="flex items-center justify-center px-2 border-r border-gray-100 bg-gray-50 flex-shrink-0">
            {dragHandle}
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex-1 flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors min-w-0"
        >
          <span className="font-semibold text-[#1E1A34] truncate">{title}</span>
          <span className="text-gray-400 text-lg ml-2 flex-shrink-0">{open ? '▲' : '▼'}</span>
        </button>
      </div>
      {open && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

interface SaveButtonProps {
  onSave: () => void;
  saved?: boolean;
}

export const SaveButton: React.FC<SaveButtonProps> = ({ onSave, saved }) => (
  <button
    type="button"
    onClick={onSave}
    className={`px-6 py-2 rounded-lg font-semibold text-sm transition-colors ${
      saved
        ? 'bg-green-500 text-white'
        : 'bg-[#DC582A] hover:bg-[#c24d24] text-white'
    }`}
  >
    {saved ? '✓ Sauvegardé !' : 'Sauvegarder'}
  </button>
);
